import { Box, Typography } from '@mui/material';
import { GlassColors } from '../glassmorphism/GlassmorphismStyles';
import { ReactComponent as LocationIcon } from '../../assets/icons/info/location_fill.svg';

type BottomBadgesProps = {
  total: number;
  available: number;
  areaLabel: string;
  isDark: boolean;
};

export function BottomBadges({
  total,
  available,
  areaLabel,
  isDark,
}: BottomBadgesProps) {
  const pillSx = {
    px: '24px',
    py: '2px',
    borderRadius: '999px',
    display: 'flex',
    flex: { xs: 1, sm: 'unset' },
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    whiteSpace: 'nowrap',
    userSelect: 'none',

    backgroundColor: '#307ABD',
    boxShadow: `
      0px 0px 34.77px 0px #0000000D inset,
      0px -0.5px 0.25px 0px #00000080 inset,
      0px 0.5px 0.75px 0px #FFFFFF inset,
      0px 2.07px 3.73px 0px #0000004D,
      0px 2.08px 2.48px -2px #00000066,
      0px 3.08px 2px -2px #00000054
  `,
    fontFamily: 'Montserrat',
    fontWeight: 500,
    fontSize: 12,
    color: GlassColors.primaryBackground,
    zIndex: 0,
  } as const;

  const pillActiveSx = {
    ...pillSx,
    background: '#00BAB8',
  } as const;

  return (
    <Box
      sx={{
        position: { xs: 'static', sm: 'absolute' },
        left: '50%',
        bottom: 14,
        transform: { xs: 'none', sm: 'translateX(-50%)' },
        mt: { xs: '8px', sm: 0 },
        zIndex: 1200,
        display: { xs: 'flex', sm: 'inline-flex' },
        flexWrap: { xs: 'wrap', sm: 'noWrap' },
        p: '8px',
        gap: 1,
        alignItems: 'center',
        justifyContent: 'center',
        px: 1,
        height: 'auto !important',
        pointerEvents: 'none',
        borderRadius: { xs: '16px', sm: '32px' },
        backgroundColor: isDark
          ? GlassColors.light.textPrimary
          : GlassColors.dark.textPrimary,
        boxShadow: `
          0px 0px 34.77px 0px #0000000D inset,
          0px -0.5px 0.25px 0px #00000080 inset,
          0px 0.5px 0.75px 0px #FFFFFF inset,
          0px 2.07px 3.73px 0px #0000004D,
          0px 2.08px 2.48px -2px #00000066,
          0px 3.08px 2px -2px #00000054
        `,
      }}
    >
      <Box sx={{ ...pillSx, pointerEvents: 'auto' }}>
        <Typography
          sx={{
            font: 'inherit',
            lineHeight: '20px',
            letterSpacing: '.4px',
          }}
        >
          {total} Professionals
        </Typography>
      </Box>

      <Box
        sx={{
          ...pillActiveSx,
          pointerEvents: 'auto',
        }}
      >
        <Typography
          sx={{
            font: 'inherit',
            lineHeight: '20px',
            letterSpacing: '.4px',
          }}
        >
          {available} Available
        </Typography>
      </Box>

      <Box
        sx={{
          ...pillSx,
          pointerEvents: 'auto',
          maxWidth: { xs: 'none', sm: 240, md: 280 },
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: { xs: '250px', sm: 'auto' },
        }}
      >
        <Typography
          sx={{
            font: 'inherit',
            lineHeight: '20px',
            letterSpacing: '.4px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'flex',
            alignItems: 'center',

            '& svg': {
              mr: '4px',
            },
          }}
        >
          <LocationIcon /> {areaLabel}
        </Typography>
      </Box>
    </Box>
  );
}
